import type { ProblemReportCapabilities } from '../../api/generated/model/problemReportCapabilities'
import { setProblemReportContext } from './runtime'

export interface ReportIdentity {
  userId: string
  membershipId: string
  orgId: string
  shell: 'admin' | 'seller' | 'supply'
  simulatedSupplyPersona?: string
}
export interface ReportHostState {
  phase: 'disabled' | 'pending' | 'ready' | 'unavailable'
  identity: Readonly<ReportIdentity> | null
  generation: number
  policy: ProblemReportCapabilities | null
}
export interface ReportRequestContext { identity: Readonly<ReportIdentity>; generation: number }
let state: ReportHostState = { phase: 'disabled', identity: null, generation: 0, policy: null }
let previousIdentity: Readonly<ReportIdentity> | null = null
const listeners = new Set<() => void>()
export const reportIdentityKey = (identity: ReportIdentity) =>
  JSON.stringify([identity.userId, identity.membershipId, identity.orgId, identity.shell])
export const reportHostState = () => state
export function subscribeReportHost(listener: () => void) {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}
function publish(next: ReportHostState) {
  state = next
  setProblemReportContext(next.identity && next.policy?.enabled && next.policy.can_self
    ? reportIdentityKey(next.identity) : null)
  for (const listener of listeners) listener()
}
export function clearReportIdentity(phase: 'disabled' | 'unavailable' = 'disabled') {
  previousIdentity = null
  publish({ phase, identity: null, generation: state.generation + 1, policy: null })
}
/** Token rotation suspends authority; only a verified canonical change drops drafts. */
export function suspendReportIdentity() {
  publish({ ...state, phase: 'pending', identity: null, policy: null })
}
export function verifyReportIdentity(identity: ReportIdentity) {
  const changed = previousIdentity === null || reportIdentityKey(previousIdentity) !== reportIdentityKey(identity)
  previousIdentity = Object.freeze({ ...identity })
  publish({ phase: 'pending', identity: previousIdentity, generation: state.generation + Number(changed), policy: null })
}
export function setReportPolicy(policy: ProblemReportCapabilities | null) {
  if (!state.identity) return
  publish({ ...state, phase: policy ? 'ready' : 'unavailable', policy })
}
export function reportContextChanged() {
  return Object.assign(new Error('Report context changed; refresh before continuing'), {
    status: 409,
    problem: { status: 409, code: 'report_context_changed', detail: 'Report context changed; refresh before continuing' },
  })
}
export function reportRequestContext(assertion?: string | null): ReportRequestContext {
  if (!state.identity) throw reportContextChanged()
  if (assertion != null && assertion !== state.identity.membershipId) {
    clearReportIdentity('unavailable')
    throw reportContextChanged()
  }
  return { identity: state.identity, generation: state.generation }
}
export function assertReportRequest(request: ReportRequestContext): void {
  if (state.generation !== request.generation || !state.identity || reportIdentityKey(state.identity) !== reportIdentityKey(request.identity)) throw reportContextChanged()
}
export async function settleReportRequest(request: ReportRequestContext, signal?: AbortSignal | null): Promise<void> {
  if (signal?.aborted) throw new DOMException('Aborted', 'AbortError')
  if (state.generation === request.generation && !state.identity && state.phase === 'pending') {
    await new Promise<void>((resolve, reject) => {
      const dispose = () => { unsubscribe(); signal?.removeEventListener('abort', abort) }
      const abort = () => { dispose(); reject(new DOMException('Aborted', 'AbortError')) }
      const unsubscribe = subscribeReportHost(() => {
        if (state.generation !== request.generation || state.identity || state.phase !== 'pending') { dispose(); resolve() }
      })
      signal?.addEventListener('abort', abort, { once: true })
    })
  }
  assertReportRequest(request)
}
/** Every report query/draft uses this scope; generation changes are destructive resets. */
export function reportQueryKey(...resource: readonly unknown[]) {
  return ['problem-reports', state.generation, previousIdentity ? reportIdentityKey(previousIdentity) : null, ...resource] as const
}
